import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useDraftMode } from "../contexts/DraftModeContext";
import { backendService } from "../services/backend.service";
import { type DraftChange, DraftChangeType, type DraftDayStatsResponse, type DraftOverlay, type DraftOverlayShift, type NurseInShift } from "../utils/models";
import { useDebouncedValue } from "./useDebouncedValue";
import { useRevealWage } from "./useRevealWage";

// Coalesce overlay churn during rapid edits so the wage backend isn't recomputed per keystroke.
const DRAFT_OVERLAY_DEBOUNCE_MS = 250;

// Sort keys so semantically identical overlays produce the same cache key.
const stableOverlayKey = (overlay: DraftOverlay): string => {
	const sortedAdds = [...overlay.adds].sort((a, b) => (a.id ?? "").localeCompare(b.id ?? ""));
	const sortedUpdates = Object.fromEntries(Object.entries(overlay.updates).sort(([a], [b]) => a.localeCompare(b)));
	return JSON.stringify({ adds: sortedAdds, removes: [...overlay.removes].sort(), updates: sortedUpdates });
};

const buildAddShift = (nurse: NurseInShift, departmentId: string): DraftOverlayShift => ({
	id: nurse.id,
	nurseId: nurse.nurseId,
	startTime: nurse.startTime?.toISO() ?? undefined,
	endTime: nurse.endTime?.toISO() ?? undefined,
	charge: nurse.charge,
	trainingStatus: nurse.trainingStatus,
	bonus: nurse.bonus,
	departmentId: nurse.departmentId ?? departmentId,
	breaks: nurse.breaks,
});

/**
 * Translate pending draft changes into a `DraftOverlay`.
 *   ADD / APPROVE_ADD_REQUEST     -> adds
 *   REMOVE                        -> removes
 *   UPDATE                        -> updates[shiftId] (changed fields only)
 *   MOVE / APPROVE_REPLACE_REQUEST -> remove + add
 *   UPDATE_CENSUS / DELETE_CENSUS -> ignored
 */
const buildOverlayFromDraftChanges = (changes: DraftChange[], departmentId: string): DraftOverlay => {
	const overlay: DraftOverlay = { adds: [], removes: [], updates: {} };

	for (const change of changes) {
		const { nurse, nurseToRemove, diff } = change.payload;

		switch (change.type) {
			case DraftChangeType.ADD:
			case DraftChangeType.APPROVE_ADD_REQUEST: {
				if (!nurse) continue;
				overlay.adds.push(buildAddShift(nurse, departmentId));
				break;
			}

			case DraftChangeType.REMOVE: {
				const removedId = nurseToRemove?.id ?? nurse?.id;
				if (removedId) overlay.removes.push(removedId);
				break;
			}

			case DraftChangeType.UPDATE: {
				if (!nurse || !diff) continue;
				const update: DraftOverlayShift = { id: nurse.id, nurseId: nurse.nurseId };
				if (diff.startTime) update.startTime = diff.startTime.toISO() ?? undefined;
				if (diff.endTime) update.endTime = diff.endTime.toISO() ?? undefined;
				if (diff.charge !== undefined) update.charge = diff.charge;
				if (diff.trainingStatus !== undefined) update.trainingStatus = diff.trainingStatus;
				if (diff.bonus !== undefined) update.bonus = diff.bonus;
				if (diff.breaks !== undefined) update.breaks = diff.breaks;
				overlay.updates[nurse.id] = update;
				break;
			}

			case DraftChangeType.MOVE:
			case DraftChangeType.APPROVE_REPLACE_REQUEST: {
				if (nurseToRemove?.id) overlay.removes.push(nurseToRemove.id);
				if (nurse) overlay.adds.push(buildAddShift(nurse, departmentId));
				break;
			}

			default:
				break;
		}
	}

	return overlay;
};

/** Live wage/OT/day-expenses for the active draft day, recomputed from the user's overlay. */
export const useDraftDayStats = (date: string | undefined, enabled = true) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isDraftMode, draftChanges } = useDraftMode();
	const { trackError } = useAnalytics();
	// BE returns 403 when wage is hidden — gate the request to avoid an error toast.
	const canViewWage = useRevealWage();

	const overlay = useMemo<DraftOverlay>(
		() => buildOverlayFromDraftChanges(draftChanges, chosenDepartment.department_id),
		[draftChanges, chosenDepartment.department_id],
	);
	const debouncedOverlay = useDebouncedValue(overlay, DRAFT_OVERLAY_DEBOUNCE_MS);
	const overlayKey = useMemo(() => stableOverlayKey(debouncedOverlay), [debouncedOverlay]);
	const isEnabled = enabled && isDraftMode && !!date && !!chosenDepartment.department_id && canViewWage;

	return useQuery<DraftDayStatsResponse>({
		queryKey: ["draft-day-stats", chosenDepartment.department_id, date, overlayKey],
		queryFn: async () => {
			// Defensive narrow: `enabled` already gates on `date`, but TS can't narrow inside this closure.
			if (!date) throw new Error("useDraftDayStats invoked without a date");
			try {
				const { data } = await backendService.getDraftDayStats({
					departmentId: chosenDepartment.department_id,
					date,
					overlay: debouncedOverlay,
				});
				return data;
			} catch (error) {
				trackError("draft_day_stats_fetch_failed", { error_message: (error as Error)?.message });
				throw error;
			}
		},
		enabled: isEnabled,
		// Keep the previous result visible while the user is mid-edit; otherwise the
		// wage breakdown UI flickers between an empty state and the new result.
		placeholderData: keepPreviousData,
		staleTime: 0,
	});
};
